import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { UpdateRequired } from './components/UpdateRequired';
import { initAnalytics } from './analytics/init';
import { initErrorReporting, reportError } from './errorReporting';
import { connectivity } from './lib/connectivity';
import { startKeyboardInset } from './lib/keyboardInset';
import { initTheme } from './lib/theme';
import { applyNativeViewport } from './native';
import './styles.css';

// App-Hülle ohne Netz verfügbar (nur im gebauten Zustand, nicht in der Entwicklung)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

// Gewählte Darstellung (System/Hell/Dunkel) vor dem ersten Rendern setzen; kein Inline-Skript wegen der CSP
initTheme();

// Nur in der App: Ansicht bis unter Notch und Gestenleiste (Safe-Area wird per CSS eingehalten)
applyNativeViewport();

// Tastatur: verdeckte Höhe als `--keyboard-inset`, fokussierte Felder bleiben sichtbar (U15d)
startKeyboardInset();

// Verbindungszustand aus echten Anfragen, Probe und Sendeanstoß bei Netz-Rückkehr (U15a)
connectivity.start();

// Fehlerberichte an GlitchTip (zentraler Meldeweg, siehe errorReporting.ts)
void initErrorReporting();

// Google Analytics nur, wenn der Server eine Mess-ID liefert; geladen wird erst nach Einwilligung (analytics/analytics.ts)
void initAnalytics();

/** Render-Fehler von React melden (abgefangene wie nicht abgefangene). */
function onReactError(error: unknown, info: { componentStack?: string | undefined }): void {
  reportError(error, { componentStack: info.componentStack });
}

const container = document.getElementById('root');
if (container === null) throw new Error('Das Element #root fehlt.');

createRoot(container, {
  onUncaughtError: (error, info) => {
    onReactError(error, info);
    // In der Entwicklung sichtbar machen (Konsole des Browsers); eine Doppelmeldung fängt dedupe ab
    if (!import.meta.env.PROD) globalThis.reportError(error);
  },
  onCaughtError: onReactError,
  onRecoverableError: onReactError,
}).render(
  <StrictMode>
    <UpdateRequired>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </UpdateRequired>
  </StrictMode>,
);
